import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { StaticQuery, graphql } from 'gatsby'
import { useInView } from 'react-intersection-observer'
import classnames from 'classnames'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'

import Image from '../components/UI/Image'

import '../styles/lightbox.css'
import styles from '../styles/sections/Structure.module.css'

const Structure = props => {
  const [ref, inView] = useInView({
    threshold: 0
  })

  const [isImageItemVisible, setIsImageItemVisible] = useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxImages, setLightboxImages] = useState([])
  const imageItemClassName = classnames(styles.imageItem, {
    [styles.imageItemVisible]: isImageItemVisible
  })

  const openLightbox = images => {
    const newLightboxImages = images.nodes.map(image => image.childImageSharp.fluid.src)
    setLightboxIndex(0)
    setLightboxImages(newLightboxImages)
    setIsLightboxOpen(true)
  }

  useEffect(() => {
    if (inView && !isImageItemVisible) {
      setIsImageItemVisible(true)
    }
  }, [inView, isImageItemVisible])

  return (
    <section id={props.markdownRemark.frontmatter.id} className={styles.section}>
      <div className={styles.container}>
        <div className={styles.textWrapper}>
          <h2 className={styles.title}>
            {props.markdownRemark.frontmatter.title}
            <small className={styles.subtitle}>{props.markdownRemark.frontmatter.subtitle}</small>
          </h2>

          <div
            className={styles.text}
            dangerouslySetInnerHTML={{ __html: props.markdownRemark.html }}
          />
        </div>

        <ul className={styles.imageWrapper} ref={ref}>
          <li className={imageItemClassName}>
            <button className={styles.imageButton} onClick={() => openLightbox(props.plantation)}>
              <Image fluid={props.plantationThumb.childImageSharp.fluid} className={styles.image} />
            </button>
          </li>

          <li className={imageItemClassName}>
            <button className={styles.imageButton} onClick={() => openLightbox(props.warehouse)}>
              <Image fluid={props.warehouseThumb.childImageSharp.fluid} className={styles.image} />
            </button>
          </li>

          <li className={imageItemClassName}>
            <button className={styles.imageButton} onClick={() => openLightbox(props.warehouse2)}>
              <Image fluid={props.warehouse2Thumb.childImageSharp.fluid} className={styles.image} />
            </button>
          </li>

          <li className={imageItemClassName}>
            <button className={styles.imageButton} onClick={() => openLightbox(props.distribution)}>
              <Image
                fluid={props.distributionThumb.childImageSharp.fluid}
                className={styles.image}
              />
            </button>
          </li>
        </ul>

        {isLightboxOpen && (
          <Lightbox
            enableZoom={false}
            mainSrc={lightboxImages[lightboxIndex]}
            nextSrc={lightboxImages[(lightboxIndex + 1) % lightboxImages.length]}
            prevSrc={
              lightboxImages[(lightboxIndex + lightboxImages.length - 1) % lightboxImages.length]
            }
            onCloseRequest={() => setIsLightboxOpen(false)}
            onMovePrevRequest={() =>
              setLightboxIndex((lightboxIndex + lightboxImages.length - 1) % lightboxImages.length)
            }
            onMoveNextRequest={() => setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)}
          />
        )}
      </div>
    </section>
  )
}

Structure.propTypes = {
  markdownRemark: PropTypes.shape({
    frontmatter: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      markers: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          lat: PropTypes.number,
          lng: PropTypes.number
        })
      )
    }),
    html: PropTypes.string
  }),
  plantationThumb: PropTypes.shape({
    childImageSharp: PropTypes.shape({
      fluid: PropTypes.object
    })
  }),
  plantation: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        childImageSharp: PropTypes.shape({
          fluid: PropTypes.shape({
            src: PropTypes.string
          })
        })
      })
    )
  }),
  warehouseThumb: PropTypes.shape({
    childImageSharp: PropTypes.shape({
      fluid: PropTypes.object
    })
  }),
  warehouse: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        childImageSharp: PropTypes.shape({
          fluid: PropTypes.shape({
            src: PropTypes.string
          })
        })
      })
    )
  }),
  warehouse2Thumb: PropTypes.shape({
    childImageSharp: PropTypes.shape({
      fluid: PropTypes.object
    })
  }),
  warehouse2: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        childImageSharp: PropTypes.shape({
          fluid: PropTypes.shape({
            src: PropTypes.string
          })
        })
      })
    )
  }),
  distributionThumb: PropTypes.shape({
    childImageSharp: PropTypes.shape({
      fluid: PropTypes.object
    })
  }),
  distribution: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        childImageSharp: PropTypes.shape({
          fluid: PropTypes.shape({
            src: PropTypes.string
          })
        })
      })
    )
  })
}

const structureQuery = graphql`
  query StructureQuery {
    markdownRemark(frontmatter: { id: { eq: "structure" } }) {
      frontmatter {
        id
        title
        subtitle
      }
      html
    }
    plantationThumb: file(relativePath: { eq: "structure/plantacao.jpeg" }) {
      childImageSharp {
        fluid(maxWidth: 786, maxHeight: 510) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    plantation: allFile(
      filter: { absolutePath: { regex: "//structure/plantacao//" } }
      sort: { fields: name }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 1600) {
            src
          }
        }
      }
    }
    warehouseThumb: file(relativePath: { eq: "structure/deposito.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 786, maxHeight: 510) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    warehouse: allFile(
      filter: { absolutePath: { regex: "//structure/deposito//" } }
      sort: { fields: name }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 1600) {
            src
          }
        }
      }
    }
    warehouse2Thumb: file(relativePath: { eq: "structure/deposito-2.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 786, maxHeight: 510) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    warehouse2: allFile(
      filter: { absolutePath: { regex: "//structure/deposito-2//" } }
      sort: { fields: name }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 1600) {
            src
          }
        }
      }
    }
    distributionThumb: file(relativePath: { eq: "structure/distribuicao.jpeg" }) {
      childImageSharp {
        fluid(maxWidth: 786, maxHeight: 510) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    distribution: allFile(
      filter: { absolutePath: { regex: "//structure/distribuicao//" } }
      sort: { fields: name }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 1600) {
            src
          }
        }
      }
    }
  }
`

export default () => <StaticQuery query={structureQuery} render={data => <Structure {...data} />} />
